import type { App, Directive } from 'vue'

interface DragOptions {
  boundary?: boolean
  snap?: boolean
}

export default function useDragDivDirective (app: App) {
  const dragWindow: Directive<HTMLElement, DragOptions> = {
    mounted (el, binding) {
      const modal = el
      const header = el.querySelector('.drag-title') as HTMLElement
      if (!header) return

      const { boundary = true, snap = false } = binding.value || {}

      header.style.cursor = 'move'
      header.style.userSelect = 'none'

      // ✅ 明确找 drag-container
      const parent = modal.closest('.drag-container') as HTMLElement
      if (!parent) {
        console.warn('[v-drag-window] 未找到 .drag-container')
        return
      }

      let startX = 0
      let startY = 0
      let startLeft = 0
      let startTop = 0

      const onPointerDown = (e: PointerEvent) => {
        e.preventDefault()

        startX = e.clientX
        startY = e.clientY

        startLeft = modal.offsetLeft
        startTop = modal.offsetTop

        document.addEventListener('pointermove', onPointerMove)
        document.addEventListener('pointerup', onPointerUp)
      }

      const onPointerMove = (e: PointerEvent) => {
        let left = startLeft + (e.clientX - startX)
        let top = startTop + (e.clientY - startY)

        if (boundary) {
          const maxLeft = parent.clientWidth - modal.offsetWidth
          const maxTop = parent.clientHeight - modal.offsetHeight

          left = Math.max(0, Math.min(left, maxLeft))
          top = Math.max(0, Math.min(top, maxTop))
        }

        modal.style.left = `${left}px`
        modal.style.top = `${top}px`
      }

      const onPointerUp = () => {
        if (snap) snapToParentEdge()
        document.removeEventListener('pointermove', onPointerMove)
        document.removeEventListener('pointerup', onPointerUp)
      }

      const snapToParentEdge = () => {
        const threshold = 20
        const left = modal.offsetLeft
        const top = modal.offsetTop

        const maxLeft = parent.clientWidth - modal.offsetWidth
        const maxTop = parent.clientHeight - modal.offsetHeight

        let finalLeft = left
        let finalTop = top

        if (left < threshold) finalLeft = 0
        if (maxLeft - left < threshold) finalLeft = maxLeft

        if (top < threshold) finalTop = 0
        if (maxTop - top < threshold) finalTop = maxTop

        modal.style.left = `${finalLeft}px`
        modal.style.top = `${finalTop}px`
      }

      header.addEventListener('pointerdown', onPointerDown)

      // 🧹 清理
      ;(el as any)._dragCleanup = () => {
        header.removeEventListener('pointerdown', onPointerDown)
        document.removeEventListener('pointermove', onPointerMove)
        document.removeEventListener('pointerup', onPointerUp)
      }
    },

    unmounted (el: any) {
      el._dragCleanup?.()
      delete el._dragCleanup
    }
  }

  app.directive('drag-div', dragWindow)
}
